import { PageLoader } from '@application/components';
import { UserContext } from '@application/contexts';
import { Routes } from '@application/routes';
import { Query, QueryTenantDashboardEmbedTokenArgs } from '@domain/graphql.types';
import { useTenantDashboardEmbedTokenQuery } from '@domain/tenants';
import { QueryTenantDashboardEmbedToken } from '@infrastructure/graphql';
import { Box } from '@mui/material';
import { EmbeddedDashboard, embedDashboard } from '@superset-ui/embedded-sdk';
import { useCallback, useContext, useEffect, useMemo, useRef } from 'react';
import { Navigate, useParams } from 'react-router';
import { useClient } from 'urql';

const SupersetDashboardView = () => {
  const { dashboardKey } = useParams();
  const { tenant } = useContext(UserContext);
  const mountPointRef = useRef<HTMLDivElement>(null);
  const embedRef = useRef<EmbeddedDashboard | null>(null);
  const client = useClient();

  const dashboard = useMemo(() => tenant?.dashboards.find((entry) => entry.key === dashboardKey), [dashboardKey, tenant?.dashboards]);

  const { data, fetching } = useTenantDashboardEmbedTokenQuery({
    variables: {
      input: {
        dashboardKey: dashboardKey || '',
      },
    },
    pause: !dashboardKey || !dashboard,
  });

  const fetchGuestToken = useCallback(async () => {
    const result = await client
      .query<Pick<Query, 'tenantDashboardEmbedToken'>, QueryTenantDashboardEmbedTokenArgs>(
        QueryTenantDashboardEmbedToken,
        { input: { dashboardKey: dashboardKey || '' } },
        { requestPolicy: 'network-only' },
      )
      .toPromise();
    const token = result?.data?.tenantDashboardEmbedToken?.token;
    if (!token) {
      throw new Error('Failed to refresh Superset guest token');
    }
    return token;
  }, [client, dashboardKey]);

  const dashboardId = data?.tenantDashboardEmbedToken?.dashboardId;
  const supersetDomain = data?.tenantDashboardEmbedToken?.supersetDomain;

  useEffect(() => {
    if (!dashboardId || !supersetDomain || !mountPointRef.current) {
      return;
    }

    const mountPoint = mountPointRef.current;

    embedDashboard({
      id: dashboardId,
      supersetDomain,
      mountPoint,
      fetchGuestToken,
      dashboardUiConfig: {
        hideTitle: true,
      },
      iframeSandboxExtras: ['allow-top-navigation', 'allow-popups-to-escape-sandbox'],
    }).then((embed) => {
      embedRef.current = embed;
    });

    return () => {
      embedRef.current?.unmount();
      embedRef.current = null;
    };
  }, [dashboardId, supersetDomain, fetchGuestToken]);

  if (!dashboardKey || !dashboard) {
    return <Navigate to={Routes.SITE_LOOKUP} replace />;
  }

  if (fetching) {
    return <PageLoader />;
  }

  return (
    <Box
      sx={{
        height: 'calc(100vh - var(--top-bar-height))',
        width: '100%',
      }}
    >
      <Box
        ref={mountPointRef}
        sx={{
          height: '100%',
          width: '100%',
          '& iframe': {
            border: 'none',
            width: '100%',
            height: '100%',
            display: 'block',
          },
        }}
      />
    </Box>
  );
};

export default SupersetDashboardView;
